import { useEffect, useMemo, useState, useRef } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";
import "./ServiceAdminDownloadLogs.scss";

function formatTs(ts) {
  if (!ts) return { date: "—", time: "—" };

  try {
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : ts?.seconds
          ? new Date(ts.seconds * 1000)
          : new Date(ts);

    return {
      date: d.toLocaleDateString("cs-CZ"),
      time: d.toLocaleTimeString("cs-CZ"),
    };
  } catch {
    return { date: "—", time: "—" };
  }
}

export default function ServiceAdminDownloadLogs() {
  const PAGE_SIZE = 5;
  const topRef = useRef(null);

  const [usersByEmail, setUsersByEmail] = useState({});
  const [logs, setLogs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  // paging
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([]);
  const [hasNext, setHasNext] = useState(false);

  async function loadUsersMapOnce() {
    try {
      const snap = await getDocs(collection(db, "users"));
      const map = {};
      snap.docs.forEach((d) => {
        const u = d.data() || {};
        if (!u.email) return;
        const fullName = `${u.name || ""} ${u.surname || ""}`.trim();
        map[u.email] = fullName || "(bez jména)";
      });
      setUsersByEmail(map);
    } catch (e) {
      console.warn("loadUsersMapOnce failed:", e);
      setUsersByEmail({});
    }
  }

  async function fetchPage(targetPage = 1) {
    setLoading(true);
    setError("");
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }

    try {
      const logsRef = collection(db, "downloadLogs");

      const cursor =
        targetPage <= 1 ? null : pageCursors[targetPage - 2] || null;

      const base = [
        where("role", "in", [
          "service",
          "service_admin",
          "firmware_admin",
          "admin",
          "super_admin",
        ]),
        orderBy("createdAt", "desc"),
      ];

      const constraints = cursor
        ? [...base, startAfter(cursor), limit(PAGE_SIZE + 1)]
        : [...base, limit(PAGE_SIZE + 1)];

      const q = query(logsRef, ...constraints);
      const snap = await getDocs(q);

      const docs = snap.docs || [];
      const docsForPage = docs.slice(0, PAGE_SIZE);

      setHasNext(docs.length > PAGE_SIZE);

      const lastDoc = docsForPage.length
        ? docsForPage[docsForPage.length - 1]
        : null;

      setPageCursors((prev) => {
        const next = [...prev];
        next[targetPage - 1] = lastDoc;
        return next;
      });

      const raw = docsForPage.map((d) => ({ id: d.id, ...d.data() }));

      const enriched = raw.map((log) => {
        const { date, time } = formatTs(log.createdAt);
        return {
          ...log,
          name: usersByEmail[log.email] || "(neznámý uživatel)",
          date,
          time,
          system: log.device || "—",
          variant: log.variant || "—",
          folder: log.channel || "—",
          file: log.filename || "—",
          originalFile: log.originalFilename || "—",
        };
      });

      setLogs(enriched);
      setPage(targetPage);
    } catch (e) {
      console.error(e);
      setError("Nepodařilo se načíst logy stažení (downloadLogs).");
      setLogs([]);
      setHasNext(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsersMapOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(usersByEmail).length]);

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;

    return logs.filter((l) => {
      const hay = [
        l.name,
        l.email,
        l.file,
        l.originalFile,
        l.system,
        l.variant,
        l.folder,
        l.ip,
        l.key,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [logs, search]);

  async function deleteLog(log) {
    if (!log?.id) return;

    const ok = window.confirm(
      `Opravdu chceš smazat tento záznam?\n\n${log.email || ""}\n${log.file || ""}\n${log.system || ""}/${log.variant || ""}/${log.folder || ""}`,
    );
    if (!ok) return;

    setBusyId(log.id);
    setError("");

    try {
      await deleteDoc(doc(db, "downloadLogs", log.id));

      await fetchPage(page);
      if (page > 1 && (!logs || logs.length <= 1)) {
        await fetchPage(page - 1);
      }
    } catch (e) {
      console.error(e);
      setError("Smazání logu se nezdařilo. Zkontroluj Firestore rules.");
    } finally {
      setBusyId(null);
    }
  }

  function goPrev() {
    if (page <= 1) return;
    fetchPage(page - 1);
  }

  function goNext() {
    if (!hasNext) return;
    fetchPage(page + 1);
  }

  return (
    <div className="service-admin-logs-page" ref={topRef}>
      <h1>Logy servisáků</h1>
      <p className="mode-line">Režim: service_admin</p>

      <div className="admin-search">
        <label htmlFor="serviceLogsSearch">Hledat:</label>
        <input
          id="serviceLogsSearch"
          type="text"
          placeholder="jméno / email / soubor / pův. název / systém / varianta / složka / IP"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <p className="error">{error}</p>}

      <div className="table-content-wrapper">
        {loading ? (
          <p>Načítání záznamů...</p>
        ) : filteredLogs.length === 0 ? (
          <p>Žádné záznamy nenalezeny.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Jméno:</th>
                <th>Email:</th>
                <th>Soubor:</th>
                <th>Původní název:</th>
                <th>Systém:</th>
                <th>Varianta:</th>
                <th>Složka:</th>
                <th>Staženo:</th>
                <th>Akce:</th>
              </tr>
            </thead>

            <tbody>
              {filteredLogs.map((log) => {
                const isBusy = busyId === log.id;

                return (
                  <tr key={log.id}>
                    <td>
                      <span className="label">Jméno:</span>
                      <span className="value">{log.name || "—"}</span>
                    </td>

                    <td>
                      <span className="label">Email:</span>
                      <span className="value">{log.email || "—"}</span>
                    </td>

                    <td>
                      <span className="label">Soubor:</span>
                      <span className="value">{log.file || "—"}</span>
                    </td>

                    <td>
                      <span className="label">Původní název:</span>
                      <span className="value">{log.originalFile || "—"}</span>
                    </td>

                    <td>
                      <span className="label">Systém:</span>
                      <span className="value">{log.system || "—"}</span>
                    </td>

                    <td>
                      <span className="label">Varianta:</span>
                      <span className="value">{log.variant || "—"}</span>
                    </td>

                    <td>
                      <span className="label">Složka:</span>
                      <span className="value">{log.folder || "—"}</span>
                    </td>

                    <td>
                      <span className="label">Staženo:</span>
                      <span className="value">
                        {log.date} {log.time}
                      </span>
                    </td>

                    <td>
                      <span className="label">Akce:</span>
                      <span className="value">
                        <div className="action-buttons">
                          <button
                            className="delete"
                            disabled={isBusy}
                            onClick={() => deleteLog(log)}
                            title="Smazat záznam"
                          >
                            {isBusy ? "..." : "Smazat"}
                          </button>
                        </div>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(logs.length > 0 || page > 1) && !loading && (
        <div className="pagination-wrapper">
          <button
            className="pagination-btn"
            onClick={goPrev}
            disabled={page <= 1 || loading}
          >
            <FaArrowLeft className="icon" />
            <span className="text">Předchozí</span>
          </button>

          <div className="page-info">
            Stránka <b>{page}</b>
          </div>

          <button
            className="pagination-btn"
            onClick={goNext}
            disabled={!hasNext || loading}
          >
            <FaArrowRight className="icon" />
            <span className="text">Další</span>
          </button>
        </div>
      )}
    </div>
  );
}
